import { Result, Context } from "CoreUtils";
import type { Ident, Content, Stack } from "GenAlphaSchema";
import { schema } from "GenAlphaSchema";
import { DepotGet, Error } from "Depot";
// Local Imports
import type { Editor } from "./editor";

//
// # DepotGetByMessage
// The `DepotGet` interface, implemented with editor messaging
//
export class DepotByMessage implements DepotGet {
  constructor(readonly editor: Editor) {}

  content = {
    // ctx is accepted for interface compatibility but not used in client-side messaging
    get: async (
      _ctx: Context,
      ident: Ident
    ): Promise<Result<Content, Error>> => {
      return this.editor.getContent(ident);
    },
  };

  // Cache PDK fetches by id. Store the promise (not the resolved value) so
  // that concurrent callers for the same id share one in-flight request — this
  // is what prevents the out-of-order race during route-nets refresh storms.
  private pdkCache = new Map<string, Promise<Result<schema.pdk.PDK, Error>>>();

  pdk = {
    get: (pdkId: string): Promise<Result<schema.pdk.PDK, Error>> => {
      const cached = this.pdkCache.get(pdkId);
      if (cached) return cached;
      const pending = this.editor.getPdk(pdkId).then((r) => {
        // Don't cache failures — a transient network blip shouldn't poison
        // every subsequent fetch for this PDK.
        if (r.err) this.pdkCache.delete(pdkId);
        return r;
      });
      this.pdkCache.set(pdkId, pending);
      return pending;
    },
  };

  stack = {
    get: async (
      pdkId: string,
      stackId: schema.StackId
    ): Promise<Result<schema.pdk.Stack, Error>> => {
      return this.editor.getStack(pdkId, stackId);
    },
  };
}
